import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { counterpartyService } from '../services/counterpartyService';
import { UpdateCounterpartyRequest, Counterparty } from '../types/counterparty';
import { useTranslation } from '../hooks/useTranslation';
import { CountrySelect } from './CountrySelect';
import { integrationService } from '../services/integrationService';

interface EditCounterpartyModalProps {
  companyId: string;
  counterparty: Counterparty | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  taxId?: string;
  vatId?: string;
  country: string;
  city: string;
  zip: string;
  address?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  comment?: string;
}

const EditCounterpartyModal: React.FC<EditCounterpartyModalProps> = ({ 
  companyId, 
  counterparty: initialCounterparty,
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm<FormData>();
  
  // Используем локальное состояние для контрагента, чтобы обновлять его после изменений
  const [counterparty, setCounterparty] = React.useState<Counterparty | null>(initialCounterparty);

  // Обновляем локальное состояние при изменении пропса
  useEffect(() => {
    setCounterparty(initialCounterparty);
  }, [initialCounterparty]);

  // Проверяем наличие конфигурации wFirma
  const { data: hasWFirmaConfig = false } = useQuery({
    queryKey: ['wfirma-config', companyId],
    queryFn: () => integrationService.hasWFirmaConfig(companyId),
    enabled: isOpen && !!companyId,
    retry: false
  });

  const updateCounterpartyMutation = useMutation({
    mutationFn: (data: UpdateCounterpartyRequest) => 
      counterpartyService.updateCounterparty(companyId, counterparty!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', companyId] });
      reset();
      onSuccess();
    },
    onError: (error) => {
      console.error('Ошибка обновления контрагента:', error);
      // Ошибка будет обработана через apiClient interceptor, который покажет toast-уведомление
      // Не показываем alert, чтобы избежать дублирования
    }
  });

  const unsyncMutation = useMutation({
    mutationFn: () => 
      counterpartyService.unsyncCounterpartyFromWFirma(companyId, counterparty!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', companyId] });
      queryClient.invalidateQueries({ queryKey: ['counterparty', companyId, counterparty!.id] });
      // Обновляем локальное состояние контрагента
      if (counterparty) {
        setCounterparty({ ...counterparty, wfirmaId: undefined });
      }
      // Обновляем данные контрагента в кэше
      if (counterparty) {
        queryClient.setQueryData(['counterparty', companyId, counterparty.id], (old: Counterparty | undefined) => {
          if (old) {
            return { ...old, wfirmaId: undefined };
          }
          return old;
        });
      }
    },
    onError: (error) => {
      console.error('Ошибка удаления синхронизации:', error);
    }
  });

  const handleUnsync = () => {
    if (!window.confirm(t('counterparty.unsyncConfirm') || 'Вы уверены, что хотите удалить синхронизацию с wFirma? После этого можно будет заново синхронизировать контрагента.')) {
      return;
    }
    unsyncMutation.mutate();
  };

  const onSubmit = (data: FormData) => {
    updateCounterpartyMutation.mutate(data);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Заполняем форму данными контрагента при открытии
  useEffect(() => {
    if (counterparty && isOpen) {
      reset({
        name: counterparty.name,
        taxId: counterparty.taxId || '',
        vatId: counterparty.vatId || '',
        country: counterparty.country || '',
        city: counterparty.city || '',
        zip: counterparty.zip || '',
        address: counterparty.address || '',
        contactPerson: counterparty.contactPerson || '',
        email: counterparty.email || '',
        phone: counterparty.phone || '',
        comment: counterparty.comment || ''
      });
    }
  }, [counterparty, isOpen, reset]);

  if (!isOpen || !counterparty) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 my-auto max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{t('counterparty.editTitle')}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.name')} *
            </label>
            <input
              type="text"
              {...register('name', { required: t('counterparty.nameRequired') })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.namePlaceholder')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.taxId')}
            </label>
            <input
              type="text"
              {...register('taxId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.taxIdPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.vatId')}
            </label>
            <input
              type="text"
              {...register('vatId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.vatIdPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.country')} *
            </label>
            <CountrySelect
              value={watch('country') || ''}
              onChange={(value) => setValue('country', value, { shouldValidate: true })}
              placeholder={t('counterparty.countryPlaceholder') || 'Выберите страну...'}
              required
              error={errors.country?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.city')} *
            </label>
            <input
              type="text"
              {...register('city', { required: t('counterparty.cityRequired') || 'Город обязателен' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.cityPlaceholder') || 'Warsaw'}
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.zip') || 'Почтовый индекс'} *
            </label>
            <input
              type="text"
              {...register('zip', { required: t('counterparty.zipRequired') || 'Почтовый индекс обязателен' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.zipPlaceholder') || '00-000'}
            />
            {errors.zip && (
              <p className="mt-1 text-sm text-red-600">{errors.zip.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.address')}
            </label>
            <input
              type="text"
              {...register('address')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.addressPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.contactPerson')}
            </label>
            <input
              type="text"
              {...register('contactPerson')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.contactPersonPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.email')}
            </label>
            <input
              type="email"
              {...register('email')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.emailPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.phone')}
            </label>
            <input
              type="tel"
              {...register('phone')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.phonePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('counterparty.comment') || 'Комментарий'}
            </label>
            <textarea
              {...register('comment')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('counterparty.commentPlaceholder') || 'Введите комментарий...'}
            />
          </div>

          {/* Информация о синхронизации с wFirma */}
          {hasWFirmaConfig && counterparty.wfirmaId && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('counterparty.wfirmaSync') || 'Синхронизация с wFirma'}
                </label>
                <button
                  type="button"
                  onClick={handleUnsync}
                  disabled={unsyncMutation.isPending}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {unsyncMutation.isPending ? (t('common.deleting') || 'Удаление...') : (t('counterparty.removeSync') || 'Удалить синхронизацию')}
                </button>
              </div>
              <p className="text-sm text-gray-600">
                {t('counterparty.wfirmaId') || 'ID в wFirma'}: <span className="font-medium">{counterparty.wfirmaId}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('counterparty.wfirmaSyncHint') || 'После удаления синхронизации можно будет заново синхронизировать контрагента'}
              </p>
            </div>
          )}

          </form>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={updateCounterpartyMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {updateCounterpartyMutation.isPending ? t('common.updating') : t('common.update')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCounterpartyModal;
